import React, { useState, useMemo } from 'react';

const TabAnticiposPendientes = ({
  anticipos,
  onConfirmarAnticipo,
  onRechazarAnticipo,
  onVerComprobante,
  loading = false
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [filtroMonto, setFiltroMonto] = useState('todos');
  const [paginaActual, setPaginaActual] = useState(1);

  const itemsPorPagina = 10;

  const anticiposFiltrados = useMemo(() => {
    let resultado = anticipos;

    // Búsqueda
    if (busqueda.trim()) {
      const termino = busqueda.toLowerCase();
      resultado = resultado.filter(a =>
        a.cliente_nombre?.toLowerCase().includes(termino) ||
        a.servicio_nombre?.toLowerCase().includes(termino)
      );
    }

    // Filtro por rango de monto
    if (filtroMonto !== 'todos') {
      resultado = resultado.filter(a => {
        const monto = parseFloat(a.monto) || 0;
        if (filtroMonto === 'bajo') return monto < 100;
        if (filtroMonto === 'medio') return monto >= 100 && monto < 500;
        if (filtroMonto === 'alto') return monto >= 500;
        return true;
      });
    }

    return resultado;
  }, [anticipos, busqueda, filtroMonto]);

  const totalPaginas = Math.ceil(anticiposFiltrados.length / itemsPorPagina);
  const anticiposPaginados = anticiposFiltrados.slice(
    (paginaActual - 1) * itemsPorPagina,
    paginaActual * itemsPorPagina
  );

  const montoTotal = anticiposFiltrados.reduce((sum, a) => sum + (parseFloat(a.monto) || 0), 0);

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Filtros y Búsqueda */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <input
          type="text"
          placeholder="Buscar cliente o servicio..."
          value={busqueda}
          onChange={(e) => {
            setBusqueda(e.target.value);
            setPaginaActual(1);
          }}
          style={{
            padding: '0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.95rem'
          }}
        />

        <select
          value={filtroMonto}
          onChange={(e) => {
            setFiltroMonto(e.target.value);
            setPaginaActual(1);
          }}
          style={{
            padding: '0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.95rem'
          }}
        >
          <option value="todos">Todos los montos</option>
          <option value="bajo">Bajo (0-100)</option>
          <option value="medio">Medio (100-500)</option>
          <option value="alto">Alto (500+)</option>
        </select>

        <button
          onClick={() => {
            setBusqueda('');
            setFiltroMonto('todos');
            setPaginaActual(1);
          }}
          style={{
            padding: '0.75rem',
            backgroundColor: '#e5e7eb',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '0.95rem',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#d1d5db'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#e5e7eb'}
        >
          Limpiar filtros
        </button>
      </div>

      {/* Tarjetas de Anticipos */}
      {anticiposPaginados.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          {anticiposPaginados.map((anticipo, idx) => (
            <div
              key={anticipo.id || idx}
              style={{
                padding: '1.5rem',
                border: '2px solid #f59e0b',
                borderRadius: '8px',
                backgroundColor: '#fffbeb',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Encabezado */}
              <div style={{ marginBottom: '1rem', borderBottom: '1px solid #fed7aa', paddingBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 0.25rem 0', color: '#92400e', fontWeight: '700' }}>
                  {anticipo.cliente_nombre}
                </h4>
                <p style={{ margin: 0, color: '#b45309', fontSize: '0.9rem' }}>
                  {anticipo.servicio_nombre}
                </p>
              </div>

              {/* Información */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div>
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#b45309' }}>
                    Monto
                  </p>
                  <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', color: '#92400e' }}>
                    ${parseFloat(anticipo.monto || 0).toFixed(2)}
                  </p>
                </div>

                <div>
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#b45309' }}>
                    Fecha de Registro
                  </p>
                  <p style={{ margin: 0, fontWeight: '600', color: '#92400e' }}>
                    {new Date(anticipo.fecha_registro).toLocaleDateString('es-ES')}
                  </p>
                </div>
              </div>

              {/* Comprobante */}
              {anticipo.comprobante && (
                <button
                  onClick={() => onVerComprobante(anticipo.comprobante)}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    marginBottom: '1rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.opacity = '0.8'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                  🧾 Ver Comprobante
                </button>
              )}

              {/* Descripción */}
              {anticipo.descripcion && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: 'rgba(180, 83, 9, 0.1)',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.85rem',
                  color: '#92400e'
                }}>
                  {anticipo.descripcion}
                </div>
              )}

              {/* Acciones */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.75rem'
              }}>
                <button
                  onClick={() => onConfirmarAnticipo(anticipo.id)}
                  disabled={loading}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s',
                    opacity: loading ? 0.6 : 1
                  }}
                  onMouseOver={(e) => !loading && (e.target.style.opacity = '0.8')}
                  onMouseOut={(e) => !loading && (e.target.style.opacity = '1')}
                >
                  ✓ Confirmar
                </button>

                <button
                  onClick={() => onRechazarAnticipo(anticipo.id)}
                  disabled={loading}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s',
                    opacity: loading ? 0.6 : 1
                  }}
                  onMouseOver={(e) => !loading && (e.target.style.opacity = '0.8')}
                  onMouseOut={(e) => !loading && (e.target.style.opacity = '1')}
                >
                  ✕ Rechazar
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '3rem 1.5rem',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          color: '#6b7280'
        }}>
          <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: '500' }}>
            ✓ No hay anticipos pendientes
          </p>
        </div>
      )}

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          <button
            onClick={() => setPaginaActual(Math.max(1, paginaActual - 1))}
            disabled={paginaActual === 1}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            ← Anterior
          </button>

          {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(pagina => (
            <button
              key={pagina}
              onClick={() => setPaginaActual(pagina)}
              style={{
                padding: '0.5rem 0.75rem',
                border: pagina === paginaActual ? '2px solid #f59e0b' : '1px solid #d1d5db',
                backgroundColor: pagina === paginaActual ? '#fed7aa' : 'white',
                color: pagina === paginaActual ? '#f59e0b' : '#6b7280',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: pagina === paginaActual ? '700' : '600'
              }}
            >
              {pagina}
            </button>
          ))}

          <button
            onClick={() => setPaginaActual(Math.min(totalPaginas, paginaActual + 1))}
            disabled={paginaActual === totalPaginas}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Siguiente →
          </button>
        </div>
      )}

      {/* Resumen */}
      <div style={{
        padding: '1.25rem',
        backgroundColor: '#fef3c7',
        borderRadius: '8px',
        border: '1px solid #fcd34d',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem'
      }}>
        <div>
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#b45309' }}>
            Anticipos pendientes
          </p>
          <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', color: '#92400e' }}>
            {anticiposPaginados.length}
          </p>
        </div>

        <div>
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#b45309' }}>
            Monto total pendiente
          </p>
          <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', color: '#92400e' }}>
            ${montoTotal.toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TabAnticiposPendientes;
